/**
 * Offline Vector Layer Manager
 *
 * Creates OpenLayers vector layers from cached GeoJSON offline packages
 * Uses simplified Mapnik-based styling for offline maps
 */

import VectorLayer from 'ol/layer/Vector';
import VectorTileLayer from 'ol/layer/VectorTile';
import VectorSource from 'ol/source/Vector';
import VectorTileSource from 'ol/source/VectorTile';
import GeoJSON from 'ol/format/GeoJSON';
import MVT from 'ol/format/MVT';
import { createXYZ } from 'ol/tilegrid';
import Feature from 'ol/Feature';
import { fromExtent as polygonFromExtent } from 'ol/geom/Polygon';
import { Style, Fill } from 'ol/style';
import { createSimpleOfflineStyle } from './simpleOfflineStyle';
import { getMvtTileData } from './offlineMapCache';


/**
 * Create vector layer from offline package features
 * @param {Object} offlinePackage - Parsed offline package
 * @param {Object} options - Layer options
 * @returns {VectorLayer}
 */
export function createOfflineVectorLayer(offlinePackage, options = {}) {
  const features = offlinePackage.features;

  if (!features || !features.features) {
    throw new Error('Invalid offline package: missing features');
  }

  // Create vector source from GeoJSON
  const vectorSource = new VectorSource({
    features: new GeoJSON({
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857'
    }).readFeatures(features)
  });

  // Create vector layer with simplified Mapnik-based style
  const vectorLayer = new VectorLayer({
    source: vectorSource,
    style: createSimpleOfflineStyle,
    zIndex: options.zIndex || 0,
    opacity: options.opacity || 1,
    renderMode: 'image',
    updateWhileInteracting: false,
    ...options
  });

  return vectorLayer;
}

/**
 * Create separate layers for each feature type
 * @param {Object} offlinePackage - Parsed offline package
 * @returns {Object} - Object with layer name keys and VectorLayer values
 */
export function createOfflineLayersByType(offlinePackage) {
  const features = offlinePackage.features;

  if (!features || !features.features) {
    throw new Error('Invalid offline package: missing features');
  }

  // Group features by layer type
  const featuresByLayer = {};

  features.features.forEach(feature => {
    const layer = feature.properties?.layer || 'other';

    if (!featuresByLayer[layer]) {
      featuresByLayer[layer] = {
        type: 'FeatureCollection',
        features: []
      };
    }

    featuresByLayer[layer].features.push(feature);
  });

  // Create separate vector layers for each type
  const layers = {};
  const layerOrder = ['landuse', 'water', 'natural', 'buildings', 'roads', 'railways', 'barriers', 'pois'];

  layerOrder.forEach((layerName, index) => {
    if (featuresByLayer[layerName]) {
      const source = new VectorSource({
        features: new GeoJSON({
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:3857'
        }).readFeatures(featuresByLayer[layerName])
      });

      layers[layerName] = new VectorLayer({
        source: source,
        style: createSimpleOfflineStyle,
        zIndex: index,
        // Keep POIs decluttered, but allow denser road/rail labels to better match raster rendering.
        declutter: layerName === 'pois',
        renderMode: 'image',
        updateWhileInteracting: false
      });
    }
  });

  return layers;
}

/**
 * Create a vector tile layer from an MVT offline package
 * @param {Object} packageData - Parsed MVT package
 * @param {Object} options - Layer options
 * @returns {VectorTileLayer}
 */
export function createOfflineMvtLayer(packageData, options = {}) {
  const metadata = packageData?.header?.metadata || {};
  const minZoom = metadata.min_zoom ?? 0;
  const maxZoom = metadata.max_zoom ?? 19;
  const format = new MVT();

  const source = new VectorTileSource({
    format,
    tileGrid: createXYZ({ minZoom, maxZoom }),
    tileUrlFunction: (tileCoord) => {
      const z = tileCoord[0];
      const x = tileCoord[1];
      const rawY = tileCoord[2];
      const y = rawY < 0 ? -rawY - 1 : rawY;
      return `offline://${z}/${x}/${y}.pbf`;
    },
    tileLoadFunction: (tile) => {
      tile.setLoader((extent, resolution, projection) => {
        const tileCoord = tile.getTileCoord();
        const z = tileCoord[0];
        const x = tileCoord[1];
        const rawY = tileCoord[2];
        const y = rawY < 0 ? -rawY - 1 : rawY;
        const tileData = getMvtTileData(packageData, z, x, y);

        if (!tileData) {
          tile.setFeatures([]);
          return;
        }

        const features = format.readFeatures(tileData, {
          extent,
          featureProjection: projection
        });
        tile.setFeatures(features);
      });
    }
  });

  return new VectorTileLayer({
    source,
    style: createSimpleOfflineStyle,
    zIndex: options.zIndex || 0,
    // Increase label/POI density for offline vector tiles to resemble the raster map more closely.
    declutter: false,
    renderMode: 'hybrid',
    ...options
  });
}

export function createOceanBackgroundLayer(options = {}) {
  // World extent in EPSG:3857
  const extent = [
    -20037508.342789244,
    -20037508.342789244,
    20037508.342789244,
    20037508.342789244
  ];
  const polygon = polygonFromExtent(extent);
  const feature = new Feature(polygon);

  const source = new VectorSource({
    features: [feature]
  });

  return new VectorLayer({
    source,
    style: new Style({
      fill: new Fill({ color: '#f8f4f0' })
    }),
    zIndex: options.zIndex || 0,
    renderMode: 'image',
    updateWhileInteracting: false
  });
}

/**
 * Get statistics about offline package
 * @param {Object} offlinePackage - Parsed offline package
 * @returns {Object} - Statistics
 */
export function getOfflinePackageStats(offlinePackage) {
  const features = offlinePackage.features?.features || [];

  const stats = {
    total: features.length,
    byLayer: {}
  };

  features.forEach(feature => {
    const layer = feature.properties?.layer || 'other';
    stats.byLayer[layer] = (stats.byLayer[layer] || 0) + 1;
  });

  return stats;
}
